/**
 * RexUIManager - Complete implementation per UI Requirements Document
 */

export default class RexUIManager {
  constructor(scene) {
    this.scene = scene;
    this.sidebarWidth = 250;
    this.sidebarGap = 20;
    this.sidebar = null;
    this.topBar = null;
    this.bottomBar = null;
    
    console.log('🎨 RexUIManager initialized for scene:', scene.scene.key);
  }

  setupSceneAppearance() {
    this.scene.cameras.main.setBackgroundColor("#2c3e50");
    console.log('🎨 Scene appearance set up');
  }

  createProfessionalHUD() {
    console.log('🎯 Creating UI per specifications...');
    
    if (!this.scene.rexUI || !this.scene.rexUI.add) {
      console.error('❌ Rex UI plugin not available');
      return;
    }
    
    console.log('✅ Rex UI plugin available');
    
    // Create all components per specification
    this.createLeftSidebar();
    this.createTopBar();
    this.createBottomBar();
    this.createNotificationZones();
    
    console.log('✅ All UI components created per specifications');
  }

  createLeftSidebar(customRounds = null) {
    const { scene } = this;
    
    console.log('🔧 Creating scrollable sidebar following Rex UI documentation steps...');
    
    // Step 1: Build child panel from bottom to top (Rex UI documentation)
    // This creates the LARGE content that will be scrolled
    const largeChildPanel = this.createScrollableRoundTiles(customRounds);
    
    // Create separate background element with proper depth
    const actualHeight = scene.cameras.main.height > 100 ? scene.cameras.main.height : 1219;
    
    const sidebarBackground = scene.add.rectangle(
      this.sidebarWidth / 2, 
      actualHeight / 2, 
      this.sidebarWidth, 
      actualHeight, 
      0x1a252f
    );
    sidebarBackground.setAlpha(0.98);
    sidebarBackground.setStrokeStyle(2, 0x00d4ff);
    sidebarBackground.setDepth(-10); // Behind everything
    
    // Step 2: Build scrollable panel WITHOUT background (Rex UI documentation)
    // The scrollable panel is a VIEWPORT that shows part of the large child
    const scrollablePanel = scene.rexUI.add.scrollablePanel({
      x: 0,
      y: 0,
      width: this.sidebarWidth,
      height: actualHeight,
      scrollMode: 'vertical',
      origin: 0,
      
      // NO background - using separate background element
      
      // Professional header (fixed, not scrollable, aligned with top bar)
      header: scene.rexUI.add.label({
        background: scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x2c3e50, 0x00d4ff, 1),
        text: scene.rexUI.add.BBCodeText(0, 0, '[b][color=#ffffff]ROUNDS[/color][/b]', {
          fontSize: '18px', // Match top bar font size
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          resolution: 3 // Higher resolution for crisp text like round tiles
        }),
        space: { left: 20, right: 20, top: 15, bottom: 15 }, // Match top bar padding
        align: 'center'
      }),
      
      // Panel configuration - this is the scrollable area
      panel: {
        child: largeChildPanel, // The large content that can be bigger than the panel
        mask: {
          padding: 1 // Mask padding to hide content outside the panel area
        }
      },
      
      // NO slider - remove the blue circle and track
      slider: false,
      
      // Scroller behavior
      scroller: {
        threshold: 10,
        slidingDeceleration: 5000,
        backDeceleration: 2000,
        pointerOutRelease: true,
        rectBoundsInteractive: true,
        dragRate: 1
      },
      
      // Mouse wheel scrolling
      mouseWheelScroller: {
        focus: true,
        speed: 0.1
      },
      
      // Spacing configuration
      space: {
        left: 5,
        right: 5,
        top: 20, // Fine-tuned to center header with top bar
        bottom: 5,
        panel: 0,
        slider: 5,
        header: 15
      },
      
      // Alignment configuration
      align: {
        header: 'center',
        panel: 'center'
      },
      
      // Expansion configuration
      expand: {
        header: true,
        panel: true
      }
    });
    
    // Step 3: Layout the scrollable panel (CRITICAL)
    scrollablePanel.layout();
    
    this.sidebar = scrollablePanel;
    this.leftSidebar = scrollablePanel;
    this.scrollablePanel = scrollablePanel;
    console.log('✅ Scrollable panel created with proper viewport and large child');
  }

  // Build child panel from bottom to top (Rex UI documentation step 1)
  createScrollableRoundTiles(customRounds = null) {
    const { scene } = this;
    
    console.log('🔧 Building large child panel for scrollable content...');
    
    // Create a large sizer that will contain all round tiles
    // This sizer can be taller than the scrollable panel, enabling scrolling
    const tilesContainer = scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { 
        item: 18, // Slightly increased space between tiles for even spacing
        left: 10, 
        right: 10, 
        top: 15, // Increased top margin to push tiles down slightly
        bottom: 10 
      }
    });
    
    // Get rounds data
    const rounds = this.getRoundsForScene(customRounds);
    
    // Store rounds data in scene for later access
    scene.roundItems = rounds.map((round, index) => ({
      roundData: round,
      index: index
    }));
    
    console.log('🔧 Adding', rounds.length, 'round tiles to scrollable content');
    
    // Create each round tile and add to container
    rounds.forEach((round, index) => {
      const tile = this.createSingleRoundTile(round, index);
      tilesContainer.add(tile, { proportion: 0, align: 'center' });
    });
    
    // Layout the container (CRITICAL for Rex UI)
    tilesContainer.layout();
    
    console.log('✅ Large child panel created with', rounds.length, 'tiles');
    return tilesContainer;
  }

  // Get rounds appropriate for current scene (overrideable by firm scenes)
  getRoundsForScene(customRounds = null) {
    if (customRounds) return customRounds;
    
    // Start with generic rounds
    const rounds = [
      { name: 'Practice', desc: 'Warm up and learn the basics', color: 0x95a5a6, id: 'practice', levels: [0] },
      { name: 'Calibration Round', desc: 'Test your skills and get calibrated', color: 0x3498db, id: 'calibration', levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
      { name: 'Basic Round', desc: 'Standard difficulty challenges', color: 0x2ecc71, id: 'basic', levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
      { name: 'Advanced Round', desc: 'Expert level challenges', color: 0xe74c3c, id: 'advanced', levels: [16, 17, 18, 19, 20] }
    ];
    
    // Add Bain rounds if user has Bain access
    const userContext = { firmAccess: this.scene.userProfile?.firmAccess || [] };
    if (this.scene.scoreStorageManager && this.scene.scoreStorageManager.hasBainAccess(userContext)) {
      rounds.push(
        { name: 'Bain Basic', desc: 'Bain fundamentals', color: 0xc41e3a, id: 'bain-basic', levels: [22, 23, 24, 25, 28, 29] },
        { name: 'Bain Advanced', desc: 'Bain frameworks & efficiency', color: 0x8b1538, id: 'bain-advanced', levels: [31, 32, 33, 34, 35, 36, 37, 38, 39, 40] }
      );
    }
    
    return rounds;
  }

  // Create professional consulting-grade round tile
  createSingleRoundTile(round, index) {
    const { scene } = this;
    
    // Get score storage manager instance
    if (!scene.scoreStorageManager) {
      // Import and create ScoreStorageManager if not exists
      console.warn('ScoreStorageManager not initialized, creating new instance');
      const ScoreStorageManager = scene.ScoreStorageManager || window.ScoreStorageManager;
      if (ScoreStorageManager) {
        scene.scoreStorageManager = new ScoreStorageManager(scene);
      }
    }
    
    // Get display data for this round based on scoring rules
    const userContext = { firmAccess: scene.userProfile?.firmAccess || [] };
    const displayData = scene.scoreStorageManager ? 
      scene.scoreStorageManager.getRoundTileDisplayData(round.id, userContext) : 
      { showScore: false, showStars: false, score: 0, stars: 0, enabled: true, greyedOut: false };
    
    // Determine tile appearance based on enabled state and grey color
    let tileColor = round.color;
    let titleColor = '#ffffff';
    let descColor = '#ffffff';
    let borderColor = 0x00d4ff;
    let interactiveEnabled = displayData.enabled;
    
    if (displayData.greyedOut) {
      tileColor = displayData.greyColor || 0x95a5a6; // Use specific grey or default practice grey
      titleColor = '#999999';
      descColor = '#777777'; 
      borderColor = 0x666666;
      interactiveEnabled = false; // Disable interaction for greyed out rounds
    }
    
    // Professional tile using Label with FIXED height for consistency
    const tile = scene.rexUI.add.label({
      width: 220,
      height: 130, // Fixed height for all tiles
      orientation: 'vertical',
      background: scene.rexUI.add.roundRectangle(0, 0, 2, 2, 12, tileColor, borderColor, 2),
      text: scene.rexUI.add.sizer({
        orientation: 'vertical',
        space: { item: 6 }, // Consistent spacing
        height: 100 // Fixed content height
      }),
      space: { left: 18, right: 18, top: 15, bottom: 15 }, // Consistent padding
      align: 'center'
    });
    
    // Get text container for professional content layout
    const textContainer = tile.getElement('text');
    
    // Executive-level title with crisp rendering (matching top bar quality)
    const titleText = scene.rexUI.add.BBCodeText(0, 0, `[b][color=${titleColor}]${round.name}[/color][/b]`, {
      fontSize: '20px', // Larger for executive presence
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      resolution: 3, // Higher resolution for crisp text like top bar
      fixedWidth: 180,
      halign: 'center'
    });
    textContainer.add(titleText, { proportion: 0, align: 'center' });
    
    // Professional description with enhanced readability
    const descText = scene.rexUI.add.BBCodeText(0, 0, `[color=${descColor}]${round.desc}[/color]`, {
      fontSize: '14px', // Increased for professional readability
      fontFamily: 'Arial, sans-serif',
      wordWrap: { width: 180 },
      lineSpacing: 5,
      halign: 'center',
      resolution: 3, // Higher resolution for crisp text
      fixedWidth: 180
    });
    textContainer.add(descText, { proportion: 0, align: 'center' });
    
    // Executive star rating system based on actual data
    if (displayData.showStars) {
      const starsContainer = scene.rexUI.add.sizer({ orientation: 'horizontal', space: { item: 4 } });
      const maxStars = 5; // Use 5-star system as per existing calculateStarRating
      for (let i = 0; i < maxStars; i++) {
        const completed = i < displayData.stars;
        const starText = completed ? '[color=#ffd700]★[/color]' : '[color=#888888]☆[/color]';
        const star = scene.rexUI.add.BBCodeText(0, 0, starText, {
          fontSize: '16px', // Larger for better visibility
          fontFamily: 'Arial, sans-serif',
          resolution: 2
        });
        starsContainer.add(star);
      }
      starsContainer.layout();
      textContainer.add(starsContainer, { proportion: 0, align: 'center' });
    }
    
    // Professional high score display based on actual data
    let scoreText = '';
    
    // Practice rounds should show no score text at all
    if (round.id === 'practice') {
      scoreText = '[color=#bbbbbb]Not scored[/color]'; // Show nothing for practice rounds
    } else if (displayData.greyedOut) {
      scoreText = '[color=#666666]Coming Soon[/color]';
    } else if (displayData.showScore && displayData.score > 0) {
      scoreText = `[b][color=#00ff88]Best Score: ${displayData.score.toLocaleString()}[/color][/b]`;
    } else {
      scoreText = '[color=#bbbbbb]Not attempted[/color]';
    }
    
    const scoreLabel = scene.rexUI.add.BBCodeText(0, 0, scoreText, {
      fontSize: '13px', // Increased for better readability
      fontFamily: 'Arial, sans-serif',
      resolution: 3, // Crisp text rendering like top bar
      fixedWidth: 180,
      halign: 'center'
    });
    textContainer.add(scoreLabel, { proportion: 0, align: 'center' });
    
    // Professional interaction effects (only if enabled)
    if (interactiveEnabled) {
      tile.setInteractive()
        .on('pointerdown', () => {
          console.log(`Round selected: ${round.name}`);
          this.handleRoundSelection(round, index);
          // Professional selection effect
          tile.getElement('background').setStrokeStyle(3, 0x00ff88);
        })
        .on('pointerover', () => {
          // Sophisticated hover effect
          tile.getElement('background').setAlpha(0.95);
          tile.getElement('background').setStrokeStyle(3, 0x3498db);
        })
        .on('pointerout', () => {
          // Return to professional state
          tile.getElement('background').setAlpha(1);
          tile.getElement('background').setStrokeStyle(2, borderColor);
        });
    } else {
      // Add disabled visual feedback
      tile.setInteractive()
        .on('pointerdown', () => {
          console.log(`Round ${round.name} is disabled`);
          // Show disabled message
          if (scene.showCenterNotification) {
            scene.showCenterNotification(`${round.name} is not yet available`, 'warning', 2000);
          }
        });
    }
    
    // Layout the professional tile
    tile.layout();
    
    return tile;
  }

  // Refresh all round tiles with updated scoring data
  refreshRoundTiles() {
    console.log('🔄 Refreshing round tiles with updated scoring data');
    
    // Check if we have a rounds container to refresh (silently skip if not ready)
    if (!this.roundsContainer && !this.scene.roundsContainer) {
      // Don't warn during initialization - just silently skip
      console.log('🔄 Rounds container not ready yet, skipping refresh');
      return;
    }
    
    const container = this.roundsContainer || this.scene.roundsContainer;
    
    // Clear existing tiles
    if (container && container.clear) {
      container.clear(true); // Clear and destroy children
    }
    
    // Recreate the scrollable round tiles using the updated scoring system
    if (this.createScrollableRoundTiles) {
      try {
        this.createScrollableRoundTiles();
        console.log('✅ Round tiles refreshed successfully');
      } catch (error) {
        console.error('Error refreshing round tiles:', error);
      }
    } else {
      console.warn('createScrollableRoundTiles method not available');
    }
  }

  // REMOVED: Old createRoundTiles method - replaced by createScrollableRoundTiles

  // Legacy method - replaced by createScrollableRoundTiles for proper scrolling support
  createRoundTiles_DEPRECATED(sidebar, customRounds = null) {
    const { scene } = this;
    
    console.log('🔧 Creating round tiles...');
    
    // Generic component should only have generic rounds - no firm-specific data
    const rounds = customRounds || [
      { name: 'Practice', desc: 'Warm up and learn the basics', color: 0x95a5a6, id: 'practice', levels: [0] },
      { name: 'Calibration Round', desc: 'Test your skills and get calibrated', color: 0x3498db, id: 'calibration', levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
      { name: 'Basic Round', desc: 'Standard difficulty challenges', color: 0x2ecc71, id: 'basic', levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
      { name: 'Advanced Round', desc: 'Expert level challenges', color: 0xe74c3c, id: 'advanced', levels: [16, 17, 18, 19, 20] }
    ];
    
    // Store rounds data in scene for later access
    scene.roundItems = rounds.map((round, index) => ({
      roundData: round,
      index: index
    }));
    
    rounds.forEach((round, index) => {
      console.log(`Creating tile ${index + 1}: ${round.name}`);
      
      // Create tile sizer per specification 3.3 - Fixed sizing
      const tile = scene.rexUI.add.sizer({
        width: 220,
        height: 120,
        orientation: 'vertical',
        space: { left: 10, right: 10, top: 10, bottom: 10, item: 6 }
      });
      
      // Add background per specification (RoundRectangle with round's color)
      const tileBg = scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, round.color, 0x00d4ff, 1);
      tile.addBackground(tileBg);
      
      // Add title per specification (round name, bold, white, 16px - smaller for better fit)
      const titleText = scene.rexUI.add.BBCodeText(0, 0, `[b][color=#ffffff]${round.name}[/color][/b]`, {
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif'
      });
      tile.add(titleText, { proportion: 0, align: 'center' });
      
      // Add description per specification (light grey, 11px - smaller for better fit)
      const descText = scene.rexUI.add.BBCodeText(0, 0, `[color=#bdc3c7]${round.desc}[/color]`, {
        fontSize: '11px',
        fontFamily: 'Arial, sans-serif',
        wordWrap: { width: 190 }
      });
      tile.add(descText, { proportion: 0, align: 'center' });
      
      // Add progression stars per specification (horizontal sizer with 3 stars)
      const starsContainer = scene.rexUI.add.sizer({
        orientation: 'horizontal',
        space: { item: 4 }
      });
      
      // Add 3 star icons per specification (gold filled or dark grey empty)
      for (let i = 0; i < 3; i++) {
        const starColor = i < 1 ? 0xffd700 : 0x666666; // First star filled for demo
        const star = scene.rexUI.add.roundRectangle(0, 0, 12, 12, 6, starColor);
        starsContainer.add(star, { proportion: 0 });
      }
      // Layout stars before adding to tile
      starsContainer.layout();
      tile.add(starsContainer, { proportion: 0, align: 'center' });
      
      // Add high score per specification (cyan for scores, grey otherwise)
      const hasScore = index === 0; // Demo: first round has score
      const scoreText = hasScore ? 
        '[color=#00d4ff]Best: 85%[/color]' : 
        '[color=#95a5a6]Not played[/color]';
      
      const scoreLabel = scene.rexUI.add.BBCodeText(0, 0, scoreText, {
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif'
      });
      tile.add(scoreLabel, { proportion: 0, align: 'center' });
      
      // Make clickable per specification
      tile.setInteractive()
        .on('pointerdown', () => {
          console.log(`Round tile clicked: ${round.name}`);
          this.handleRoundSelection(round, index);
          // Update border for selected state (3px cyan)
          tileBg.setStrokeStyle(3, 0x00d4ff);
        })
        .on('pointerover', () => {
          // Hover effect
          tileBg.setStrokeStyle(2, 0x3498db);
        })
        .on('pointerout', () => {
          // Return to normal (1px border)
          tileBg.setStrokeStyle(1, 0x00d4ff);
        });
      
      // No firm-specific logic in generic component
      
      // CRITICAL: Layout tile before adding to sidebar (Rex UI best practice)
      tile.layout();
      sidebar.add(tile, { proportion: 0, align: 'center', padding: { bottom: 10 } });
      
      console.log(`✅ Tile ${index + 1} created and added to sidebar`);
    });
    
    console.log('✅ All round tiles created');
  }

  createTopBar() {
    const { scene } = this;
    
    console.log('🔧 Creating top bar per specification 3.4...');
    
    // Calculate canvas area positioning (centered over white canvas, not at sidebar edge)
    const canvasAreaStart = this.sidebarWidth + this.sidebarGap;
    const canvasAreaWidth = scene.cameras.main.width - canvasAreaStart - 20;
    const canvasAreaCenterX = canvasAreaStart + (canvasAreaWidth / 2);
    
    const topBarWidth = Math.min(canvasAreaWidth * 0.9, 800); // Max 800px, 90% of canvas area
    
    // Create top bar as Label following Rex UI best practices
    const topBar = scene.rexUI.add.label({
      width: topBarWidth,
      height: 60,
      orientation: 'horizontal',
      background: scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x1a252f, 0x00d4ff, 2),
      text: scene.rexUI.add.BBCodeText(0, 0, '[b][color=#ffffff]PPT Hero - Professional Training Mode[/color][/b]', {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif'
      }),
      space: { left: 20, right: 20, top: 15, bottom: 15 }
    });
    
    // CRITICAL: Layout first, then position (Rex UI best practice)
    topBar.layout();
    topBar.setPosition(canvasAreaCenterX, 50); // Centered over canvas area
    
    this.topBar = topBar;
    console.log('✅ Top bar created and centered over canvas area');
  }

  createBottomBar() {
    const { scene } = this;
    
    console.log('🔧 Creating bottom bar per specification 3.5...');
    
    // Calculate canvas area positioning (centered over white canvas area)
    const canvasAreaStart = this.sidebarWidth + this.sidebarGap;
    const canvasAreaWidth = scene.cameras.main.width - canvasAreaStart - 20;
    const canvasAreaCenterX = canvasAreaStart + (canvasAreaWidth / 2);
    
    const bottomBarWidth = Math.min(canvasAreaWidth * 0.9, 800); // Max 800px, 90% of canvas area
    const bottomBarY = scene.cameras.main.height - 50; // Match top bar distance (50px from top = 50px from bottom)
    
    // Create bottom bar sizer per Rex UI specification
    const bottomBar = scene.rexUI.add.sizer({
      width: bottomBarWidth,
      height: 60,
      orientation: 'horizontal',
      space: { left: 20, right: 20, top: 10, bottom: 10, item: 15 }
    });
    
    // Add background per specification (identical styling to sidebar and top bar)
    const bottomBarBg = scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x1a252f, 0x00d4ff, 2);
    bottomBar.addBackground(bottomBarBg);
    
    // Create buttons per specification (five professional buttons, evenly spaced)
    const buttons = ['START', 'RESTART', 'SHORTCUTS', 'ANALYTICS', 'MODE'];
    
    buttons.forEach(buttonText => {
      const button = scene.rexUI.add.label({
        background: scene.rexUI.add.roundRectangle(0, 0, 2, 2, 6, 0x34495e, 0x3498db, 1),
        text: scene.rexUI.add.BBCodeText(0, 0, `[b][color=#ffffff]${buttonText}[/color][/b]`, {
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif'
        }),
        space: { left: 15, right: 15, top: 10, bottom: 10 },
        align: 'center' // This centers the text within the label
      });
      
      // Add functionality per specification
      button.setInteractive()
        .on('pointerdown', () => this.handleBottomBarButtonClick(buttonText))
        .on('pointerover', () => button.getElement('background').setFillStyle(0x2c3e50))
        .on('pointerout', () => button.getElement('background').setFillStyle(0x34495e));
      
      // CRITICAL: Layout each button before adding to sizer (Rex UI best practice)
      button.layout();
      bottomBar.add(button, { proportion: 1, expand: true });
    });
    
    // CRITICAL: Layout first, then position (Rex UI best practice)
    bottomBar.layout();
    bottomBar.setPosition(canvasAreaCenterX, bottomBarY); // Centered over canvas area
    
    this.bottomBar = bottomBar;
    console.log('✅ Bottom bar created and centered over canvas area');
  }

  handleRoundSelection(round, index) {
    console.log('🎯 Round selected per specification:', round.name);
    
    // Map round names to scene round IDs - generic only
    const roundMapping = {
      'Practice': 'practice',
      'Calibration Round': 'calibration', 
      'Basic Round': 'basic',
      'Advanced Round': 'advanced'
    };
    
    // Check if this is a firm-specific round that the user has access to
    let roundId = roundMapping[round.name];
    if (!roundId) {
      // This might be a firm-specific round - check user permissions
      if (this.scene.userProfile && this.scene.userProfile.firmAccess) {
        const firmAccess = this.scene.userProfile.firmAccess;
        // Convert round name to potential round ID (e.g., "Bain Basic" -> "bain-basic")
        const potentialRoundId = round.name.toLowerCase().replace(/\s+/g, '-');
        // Check if user has access to the firm (e.g., "bain-basic" -> check for "bain")
        const firmName = potentialRoundId.split('-')[0];
        if (firmAccess.includes(firmName)) {
          roundId = potentialRoundId;
        }
      }
    }
    
    // If still no roundId, fall back to generic
    if (!roundId) {
      roundId = round.name.toLowerCase();
    }
    
    // Create round data structure expected by the scene
    const roundData = {
      id: roundId,
      name: round.name,
      desc: round.desc,
      color: round.color,
      levels: this.getRoundLevels(roundId)
    };
    
    // Set up the scene's round data directly (bypass selectRound's roundItems dependency)
    this.scene.currentRound = roundId;
    this.scene.currentRoundData = roundData;
    
    // Clear any existing level state
    this.scene.currentLevel = null;
    this.scene.levelComplete = false;
    
    // Clear existing shapes
    if (this.scene.allShapes) {
      this.scene.allShapes.forEach(shape => {
        if (shape && shape.destroy) {
          shape.destroy();
        }
      });
    }
    this.scene.allShapes = [];
    this.scene.selectedShapes = [];
    
    // Handle round-specific logic
    if (roundId === 'calibration' && this.scene.hasCompletedCalibration) {
      this.scene.showCenterNotification('Calibration completed! Cannot replay.', 'warning', 3000);
      return;
    }
    
    // Special handling for Practice mode - load the practice level immediately
    if (roundId === 'practice') {
      this.scene.currentLevel = 0; // Practice is level 0
      this.scene.gameState = 'playing'; // Set to playing state for keyboard input
      this.scene.inputEnabled = true; // Enable input for practice mode
      this.scene.goMessageShown = true; // Enable all shortcuts including sequential ones
      this.scene.startTime = Date.now() / 1000; // Set start time for practice mode
      this.scene.timeLimit = 0; // No time limit for practice
      
      if (this.scene.createLevelShapes) {
        this.scene.createLevelShapes(); // Create practice shapes immediately
      }
      
      // Practice mode: No instructions, just shapes - clean slate for practice
    } else {
      // Show round selection confirmation
      this.scene.showCenterNotification(`${round.name} round selected. Press START to begin!`, 'info', 3000);
    }
    
    // Update top bar text per specification (Round Selected state) while preserving score
    if (this.topBar) {
      const titleElement = this.topBar.getElement('text');
      if (titleElement) {
        const score = this.scene.score || 0;
        const scoreText = score > 0 ? ` • Score: ${score}` : '';
        titleElement.setText(`[b][color=#ffffff]PPT Hero - ${round.name} - Ready${scoreText}[/color][/b]`);
        this.topBar.layout();
      }
    }
    
    console.log('Round selected:', round.name, 'Ready to start');
  }
  
  getRoundLevels(roundId) {
    // Return appropriate level arrays based on round type - matching RoundsManager
    switch (roundId) {
      case 'practice':
        return [0]; // Practice level
      case 'calibration':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; // Calibration levels
      case 'basic':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; // Basic levels - start from level 1
      case 'advanced':
        return [16, 17, 18, 19, 20]; // Advanced levels
      default:
        // Check if this is a firm-specific round that the user has access to
        if (this.scene.userProfile && this.scene.userProfile.firmAccess) {
          const firmAccess = this.scene.userProfile.firmAccess;
          // Check if user has access to the firm (e.g., "bain-basic" -> check for "bain")
          const firmName = roundId.split('-')[0];
          if (firmAccess.includes(firmName)) {
            // Return levels based on the round ID - let the scene handle the mapping
            return this.scene.getFirmSpecificLevels(roundId);
          }
        }
        return [0];
    }
  }

  handleBottomBarButtonClick(buttonText) {
    console.log('🔘 Button clicked per specification:', buttonText);
    
    // Handle button functionality per specification 3.5
    switch (buttonText) {
      case 'START':
        console.log('🚀 Starting selected round...');
        if (!this.scene.currentRound) {
          this.scene.showCenterNotification('Please select a round from the sidebar first!', 'warning', 3000);
          return;
        }
        
        // Handle different round types
        if (this.scene.currentRound === 'practice') {
          this.scene.showCenterNotification('Practice mode: Use RESTART to reset shapes. No progression.', 'info', 3000);
          return;
        }
        
        // For all other rounds, start the first level
        if (this.scene.currentRoundData && this.scene.currentRoundData.levels) {
          const firstLevelIndex = this.scene.currentRoundData.levels[0];
          console.log('Starting round:', this.scene.currentRound, 'first level:', firstLevelIndex);
          
          // Track round attempt when starting
          this.scene.trackRoundAttempt(this.scene.currentRound);
          
          this.scene.startLevel(firstLevelIndex);
        } else {
          console.error('No round data available for starting level');
          this.scene.showCenterNotification('No level data available for this round', 'warning', 3000);
        }
        break;
      case 'RESTART':
        console.log('🔄 Restarting current level...');
        this.scene.restartLevel();
        break;
      case 'SHORTCUTS':
        console.log('❓ Opening shortcut help dialog...');
        this.scene.showShortcutsPopup();
        break;
      case 'ANALYTICS':
        console.log('📊 Opening player analytics overlay...');
        this.scene.showAnalyticsPopup();
        break;
      case 'MODE':
        console.log('🔄 Toggling game modes...');
        this.scene.toggleMode();
        break;
    }
  }

  handleResize(width, height) {
    console.log('🔄 Resize:', width, 'x', height);
    
    // Reposition components maintaining specification layout
    if (this.sidebar) {
      // For ScrollablePanel, only reposition - DO NOT call setSize or layout
      // ScrollablePanel manages its own internal layout
      this.sidebar.setPosition(0, 0); // Keep at top-left
      console.log('🔧 Sidebar repositioned (ScrollablePanel manages its own layout)');
      console.log('🔧 Sidebar repositioned and resized to:', this.sidebarWidth, 'x', height);
    }
    
    if (this.topBar) {
      // Recalculate canvas area center positioning
      const canvasAreaStart = this.sidebarWidth + this.sidebarGap;
      const canvasAreaWidth = width - canvasAreaStart - 20;
      const canvasAreaCenterX = canvasAreaStart + (canvasAreaWidth / 2);
      
      this.topBar.setPosition(canvasAreaCenterX, 50); // Centered over canvas area
      console.log('🔧 Top bar repositioned to center:', canvasAreaCenterX, 50);
    }
    
    if (this.bottomBar) {
      // Recalculate canvas area center positioning
      const canvasAreaStart = this.sidebarWidth + this.sidebarGap;
      const canvasAreaWidth = width - canvasAreaStart - 20;
      const canvasAreaCenterX = canvasAreaStart + (canvasAreaWidth / 2);
      const bottomBarY = height - 50; // Match top bar distance from edges
      
      this.bottomBar.setPosition(canvasAreaCenterX, bottomBarY); // Centered over canvas area
      console.log('🔧 Bottom bar repositioned to center:', canvasAreaCenterX, bottomBarY);
    }
    
    // Reposition notification zones
    this.repositionNotificationZones(width, height);
    
    console.log('✅ Rex UI components repositioned and re-layouted');
  }

  createNotificationZones() {
    const { scene } = this;
    console.log('🔧 Creating notification zones per Rex UI specification...');
    
    // Calculate slide area center for proper positioning
    const sidebarWidth = this.sidebarWidth;
    const sidebarGap = this.sidebarGap;
    const topBarHeight = 80;
    const bottomBarHeight = 80;
    
    const availableWidth = scene.cameras.main.width - sidebarWidth - sidebarGap;
    const availableHeight = scene.cameras.main.height - topBarHeight - bottomBarHeight;
    
    const slideX = sidebarWidth + sidebarGap + (availableWidth / 2);
    const slideY = topBarHeight + (availableHeight / 2);
    
    // Instructions overlay (positioned in slide area center)
    scene.instructionsOverlay = scene.add.container(slideX, slideY);
    scene.instructionsOverlay.setDepth(2000);
    scene.instructionsOverlay.setVisible(false);

    // Remove old notification container - we'll use Rex UI Labels directly
    scene.centerNotifications = null;

    // Timer display (positioned in slide area, top-right)
    const timerX = slideX + (availableWidth * 0.85 / 2) - 25; // Right edge of slide area
    const timerY = slideY - (availableHeight * 0.85 / 2) + 30; // Top edge of slide area
    
    scene.timerDisplay = scene.add.text(timerX, timerY, "", {
      ...scene.getProfessionalTextStyle("20px", "#ffffff", "600"),
      backgroundColor: "#e74c3c",
      padding: { x: 12, y: 6 }
    });
    scene.timerDisplay.setOrigin(1, 0);
    scene.timerDisplay.setDepth(1200);
    scene.timerDisplay.setVisible(false);

    // Progress display (positioned in slide area, bottom-right)
    const progressX = timerX;
    const progressY = slideY + (availableHeight * 0.85 / 2) - 30; // Bottom edge of slide area
    
    scene.progressDisplay = scene.add.text(progressX, progressY, "", {
      ...scene.getProfessionalTextStyle("16px", "#ffffff", "600"),
      backgroundColor: "#3498db",
      padding: { x: 10, y: 6 },
      align: "right"
    });
    scene.progressDisplay.setOrigin(1, 1);
    scene.progressDisplay.setDepth(1200);
    scene.progressDisplay.setVisible(false);
    
    console.log('✅ Notification zones created and positioned in slide area');
  }

  repositionNotificationZones(width, height) {
    const { scene } = this;
    
    // Recalculate slide area center
    const sidebarWidth = this.sidebarWidth;
    const sidebarGap = this.sidebarGap;
    const topBarHeight = 80;
    const bottomBarHeight = 80;
    
    const availableWidth = width - sidebarWidth - sidebarGap;
    const availableHeight = height - topBarHeight - bottomBarHeight;
    
    const slideX = sidebarWidth + sidebarGap + (availableWidth / 2);
    const slideY = topBarHeight + (availableHeight / 2);
    
    // Reposition notification zones
    if (scene.instructionsOverlay) {
      scene.instructionsOverlay.setPosition(slideX, slideY);
    }
    
    if (scene.centerNotifications) {
      scene.centerNotifications.setPosition(slideX, slideY - 50);
    }
    
    if (scene.timerDisplay) {
      const timerX = slideX + (availableWidth * 0.85 / 2) - 25;
      const timerY = slideY - (availableHeight * 0.85 / 2) + 30;
      scene.timerDisplay.setPosition(timerX, timerY);
    }
    
    if (scene.progressDisplay) {
      const progressX = slideX + (availableWidth * 0.85 / 2) - 25;
      const progressY = slideY + (availableHeight * 0.85 / 2) - 30;
      scene.progressDisplay.setPosition(progressX, progressY);
    }
  }

  toggleMode() {
    console.log('🔄 Toggle mode requested');
    // Import the PPTHeroSceneManager to handle mode switching
    import('../PPTHeroSceneManager.js').then(async module => {
      const PPTHeroSceneManager = module.default;
      const currentMode = PPTHeroSceneManager.getCurrentMode();
      const newMode = PPTHeroSceneManager.toggleMode();
      
      console.log('🔄 Mode toggled from:', currentMode, 'to:', newMode);
      
      // Show notification about mode change (use sync version for immediate UI feedback)
      const modeDisplayName = PPTHeroSceneManager.getModeDisplayNameSync(newMode);
      this.scene.showCenterNotification(`Switching to ${modeDisplayName} mode...`, 'info', 1500);
      
      // Proper architecture: Switch to the appropriate scene based on mode and firm (async)
      const targetSceneKey = await PPTHeroSceneManager.getSceneKey(newMode);
      const userFirm = await PPTHeroSceneManager.getUserFirm();
      console.log('🔄 Switching to scene:', targetSceneKey, 'for mode:', newMode, 'firm:', userFirm);
      
      // Preserve current game state
      const currentState = {
        score: this.scene.score || 0,
        currentRound: this.scene.currentRound,
        currentLevel: this.scene.currentLevel,
        gameMode: newMode
      };
      
      // Switch to the appropriate scene (Generic or Bain)
      setTimeout(() => {
        this.scene.scene.start(targetSceneKey, currentState);
      }, 1000); // Brief delay for notification
      
    }).catch(error => {
      console.error('Error toggling mode:', error);
    });
  }



  createAnalyticsPopup() {
    console.log('📊 Creating analytics popup');
    
    // Feature flag to use new Performance Rating System
    const useNewPerformanceRating = localStorage.getItem('pptHeroUseNewAnalytics') === 'true';
    
    if (useNewPerformanceRating) {
      console.log('🆕 Using new Performance Rating System');
      this.createNewPerformanceRatingOverlay();
      return;
    }
    
    console.log('📊 Using legacy analytics system');
    
    // Legacy analytics system
    import("../../../firebaseConfig.js").then(async ({ auth }) => {
      try {
        const user = auth.currentUser;
        if (user) {
          console.log('📊 Loading legacy analytics for user:', user.uid);
          
          // Import and use legacy analytics
          const { populateAnalyticsOverlay } = await import("../../../ui/analyticsUI.js");
          await populateAnalyticsOverlay(user.uid, "PPTHero");
          
          // Show legacy overlay
          if (window.UIController && window.UIController.showOverlay) {
            window.UIController.showOverlay('analytics-overlay');
          } else {
            const overlay = document.getElementById('analytics-overlay');
            if (overlay) overlay.style.display = 'flex';
          }
        } else {
          this.scene.showCenterNotification('Please log in to view analytics', 'warning', 3000);
        }
      } catch (error) {
        console.error('Error creating legacy analytics popup:', error);
        this.scene.showCenterNotification('Error loading analytics data', 'error', 3000);
      }
    }).catch(error => {
      console.error('Error importing analytics modules:', error);
      // Fallback to basic display
      const analyticsData = this.scene.analyticsData || {};
      const message = `Analytics Summary:
• Current Score: ${this.scene.score || 0}
• Successful Shortcuts: ${analyticsData.successfulShortcuts || 0}
• Total Attempts: ${analyticsData.shortcutAttempts || 0}
• Mouse Clicks: ${analyticsData.mouseClicks || 0}
• Keystroke Count: ${analyticsData.keystrokeCount || 0}`;
      this.scene.showCenterNotification(message, 'info', 6000);
    });
  }

  // Create the new Performance Rating System overlay
  createNewPerformanceRatingOverlay() {
    console.log('🆕 Creating Performance Rating System overlay');
    
    // Create or get the performance rating system
    if (!this.scene.performanceRatingSystem) {
      // Import and create PerformanceRatingSystem
      import("../modules/PerformanceRatingSystem.js").then(({ default: PerformanceRatingSystem }) => {
        this.scene.performanceRatingSystem = new PerformanceRatingSystem(this.scene);
        this.scene.performanceRatingSystem.showPerformanceOverlay();
      }).catch(error => {
        console.error('Error loading Performance Rating System:', error);
        this.scene.showCenterNotification('Performance Rating System not available', 'error', 3000);
      });
    } else {
      this.scene.performanceRatingSystem.showPerformanceOverlay();
    }
  }

  createInstructionsPanel(instructions, levelName) {
    console.log('🔧 [TIMING] Creating instructions panel for:', levelName, 'at', Date.now());
    
    // DON'T clear here - let the scene handle clearing to avoid double-clearing race condition
    
    // Use ONLY the persistent overlay system - no center notifications
    const instructionText = instructions || `Level: ${levelName || 'Unknown'}\nComplete the objectives using keyboard shortcuts!`;
    
    // Format any shortcuts in the instruction text to show sequential format (Alt,H,G,A,R instead of Alt+H+G+A+R)
    const formattedInstructionText = instructionText.replace(/Alt\+([H-Z])\+([A-Z])\+([A-Z])\+([A-Z])/g, 'Alt,$1,$2,$3,$4');
    
    const enhancedInstructions = `📋 ${levelName || 'Level Instructions'}\n\n${formattedInstructionText}\n\n💡 Shortcuts will be available after countdown (3-2-1-GO!)`;
    
    // Use dedicated instructions overlay for clean, non-overlapping display
    if (this.scene.showLevelInstructions) {
      this.scene.showLevelInstructions(enhancedInstructions, levelName);
    }
    
    // Update the top bar to show the level name while preserving score
    if (this.topBar) {
      const titleElement = this.topBar.getElement('text');
      if (titleElement) {
        const score = this.scene.score || 0;
        const scoreText = score > 0 ? ` • Score: ${score}` : '';
        titleElement.setText(`[b][color=#ffffff]PPT Hero - ${levelName}${scoreText}[/color][/b]`);
        this.topBar.layout();
      }
    }
  }
  
  createGameFlowPanel() {
    console.log('🔧 Creating game flow panel');
    // Game flow panel now handled by instructions overlay - no additional notifications needed
  }
  
  createTimerPanel(timeLimit) {
    console.log('🔧 Creating timer panel with limit:', timeLimit);
    
    // Create timer display if it doesn't exist
    if (!this.scene.timerDisplay && timeLimit > 0) {
      this.scene.timerDisplay = this.scene.add.text(
        this.scene.cameras.main.centerX, 
        100, 
        `Time: ${timeLimit}s`,
        this.getProfessionalTextStyle(24, '#ffffff')
      ).setOrigin(0.5);
    } else if (this.scene.timerDisplay && timeLimit > 0) {
      this.scene.timerDisplay.setVisible(true);
      this.scene.timerDisplay.setText(`Time: ${timeLimit}s`);
    }
  }

  clearInstructionsPanel() { 
    console.log('🧹 [TIMING] clearInstructionsPanel called at', Date.now());
    // Clear any instruction displays - but don't destroy the container
    if (this.scene.instructionsOverlay) {
      console.log('🧹 [TIMING] Clearing Rex UI instructions overlay');
      this.scene.instructionsOverlay.removeAll(true);
      this.scene.instructionsOverlay.setVisible(false);
      this.scene.instructionsOverlay.setAlpha(0);
    }
    
    // CRITICAL FIX: Don't call clearLevelInstructions here - it causes immediate fade-out
    // The base scene will handle its own instruction clearing with proper timing
    console.log('🧹 [TIMING] Skipping clearLevelInstructions to prevent flash');
  }
  
  clearTimerPanel() { 
    console.log('🧹 clearTimerPanel');
    if (this.scene.timerDisplay) {
      this.scene.timerDisplay.setVisible(false);
    }
  }
  
  clearGameFlowPanel() { 
    console.log('🧹 clearGameFlowPanel');
    // Clear any game flow displays
    if (this.scene.progressDisplay) {
      this.scene.progressDisplay.setVisible(false);
    }
  }
  
  updateGameFlowStatus(statusText) {
    console.log('🔄 updateGameFlowStatus:', statusText);
    // Update the top bar with current status
    if (this.topBar) {
      const titleElement = this.topBar.getElement('text');
      if (titleElement) {
        titleElement.setText(`[b][color=#ffffff]PPT Hero - ${statusText}[/color][/b]`);
        this.topBar.layout();
      }
    }
  }

  // Compatibility methods - FIXED to use existing rich implementations
  refreshCalibrationTile() { console.log('🔄 refreshCalibrationTile'); }
  prepareForRender() { console.log('🎨 prepareForRender'); }
  createTopBarInfo() { 
    // FIXED: Use existing rich createTopBar implementation instead of stub
    if (!this.topBar) {
      this.createTopBar();
    }
    console.log('🔧 createTopBarInfo - rich top bar created');
  }
  updateTopBarWithInstructions(instructions, levelNumber, roundName) { 
    // Update top bar per specification (In-Game state)
    if (this.topBar) {
      const titleElement = this.topBar.getElement('text');
      if (titleElement) {
        titleElement.setText(`[b][color=#ffffff]PPT Hero - ${roundName} - Level ${levelNumber} of [Y][/color][/b]`);
        this.topBar.layout();
      }
    }
  }
  getProfessionalTextStyle(size, color, weight) { return { fontSize: size || '16px', fill: color || '#ffffff', fontWeight: weight || 'normal', fontFamily: 'Arial' }; }
  updateRoundTileProgress(roundIndex, progress) { console.log('🔄 updateRoundTileProgress'); }
  showTimerDisplay(timeRemaining, isUrgent) { 
    // FIXED: Use existing rich timer display instead of just logging
    if (this.scene.timerDisplay) {
      // Fix NaN issue by ensuring timeRemaining is always a valid number
      const validTimeRemaining = (typeof timeRemaining === 'number' && !isNaN(timeRemaining)) ? Math.max(0, timeRemaining) : 60;
      
      const minutes = Math.floor(validTimeRemaining / 60);
      const seconds = Math.floor(validTimeRemaining % 60);
      const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      // Update text and styling based on urgency
      this.scene.timerDisplay.setText(`⏰ ${timeString}`);
      this.scene.timerDisplay.setStyle({
        backgroundColor: isUrgent ? "#e74c3c" : "#27ae60", // Red if urgent, green if normal
        ...this.scene.getProfessionalTextStyle("20px", "#ffffff", "600")
      });
      this.scene.timerDisplay.setVisible(true);
      
      // Throttle logging to prevent spam (only log every 5 seconds)
      if (!this.lastTimerLog || Date.now() - this.lastTimerLog > 5000) {
        this.lastTimerLog = Date.now();
        console.log(`⏰ Timer display updated: ${timeString} (${isUrgent ? 'URGENT' : 'normal'})`);
      }
    }
  }
  hideTimerDisplay() { 
    // FIXED: Actually hide the rich timer display
    if (this.scene.timerDisplay) {
      this.scene.timerDisplay.setVisible(false);
    }
    // Only log once per hide action
    if (!this.timerHidden) {
      console.log('⏰ Timer display hidden'); 
      this.timerHidden = true;
    }
  }
  showProgressDisplay(currentLevel, totalLevels, efficiency) { 
    // FIXED: Use existing rich progress display instead of just logging
    if (this.scene.progressDisplay) {
      let progressText = `Level ${currentLevel} / ${totalLevels}`;
      if (efficiency !== null && efficiency !== undefined) {
        progressText += ` • ${efficiency}% efficiency`;
      }
      
      this.scene.progressDisplay.setText(`📊 ${progressText}`);
      this.scene.progressDisplay.setVisible(true);
      
      // Throttle logging to prevent spam (only log every 5 seconds)
      if (!this.lastProgressLog || Date.now() - this.lastProgressLog > 5000) {
        this.lastProgressLog = Date.now();
        console.log(`📊 Progress display updated: ${progressText}`);
      }
    }
  }
  hideProgressDisplay() { 
    // FIXED: Actually hide the rich progress display
    if (this.scene.progressDisplay) {
      this.scene.progressDisplay.setVisible(false);
    }
    console.log('📊 Progress display hidden'); 
  }
  updateTopBarInfo(roundName, levelNumber, score) { 
    // Throttle logging to prevent spam (only log every 5 seconds)
    if (!this.lastTopBarLog || Date.now() - this.lastTopBarLog > 5000) {
      this.lastTopBarLog = Date.now();
      console.log('📝 updateTopBarInfo (throttled):', roundName, levelNumber, score);
    }
    
    // Update the top bar with current game info
    if (this.topBar) {
      const titleElement = this.topBar.getElement('text');
      if (titleElement) {
        const scoreText = score !== undefined && score !== null ? ` • Score: ${score}` : '';
        titleElement.setText(`[b][color=#ffffff]${roundName} - Level ${levelNumber}${scoreText}[/color][/b]`);
        this.topBar.layout();
      }
    }
  }
  updateObjectiveDisplay(objective) { console.log('🎯 updateObjectiveDisplay'); }
  showSkipCalibrationPrompt() { console.log('⏭️ showSkipCalibrationPrompt'); }
  // REMOVED: Stub was overriding real analytics implementation
  clearInstructionsPanel() { console.log('🧹 clearInstructionsPanel'); }
  clearTimerPanel() { console.log('🧹 clearTimerPanel'); }
  clearGameFlowPanel() { console.log('🧹 clearGameFlowPanel'); }
  updateScore(score) { console.log('📊 updateScore'); }
  updateGameContext(text) { console.log('🎮 updateGameContext'); }
  destroy() { console.log('💥 destroy'); }
  updateTimer(timeRemaining, isUrgent) {
    // Only update if timer display exists and is visible
    if (this.scene.timerDisplay && this.scene.timerDisplay.visible) {
      // Format time consistently with "Time:" prefix
      const minutes = Math.floor(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      this.scene.timerDisplay.setText(`Time: ${timeString}`);
      
      if (isUrgent) {
        this.scene.timerDisplay.setStyle({ backgroundColor: "#e74c3c" });
      } else {
        this.scene.timerDisplay.setStyle({ backgroundColor: "#e67e22" }); // Orange background
      }
    }
  }
  // Rex UI notification system - positioned at timer level
  showNotification(text, type = 'success', duration = 2000) {
    // Clear existing notification
    if (this.scene.currentNotification) {
      this.scene.currentNotification.destroy();
      this.scene.currentNotification = null;
    }
    
    // Position at same Y level as timer
    const slideArea = this.scene.slideArea;
    if (!slideArea) return;
    
    const bgColor = type === 'success' ? 0x27ae60 : (type === 'info' ? 0x3498db : 0xe74c3c);
    
    // Create Rex UI Label ABOVE the white canvas, not inside it
    this.scene.currentNotification = this.scene.rexUI.add.label({
      x: slideArea.x,
      y: slideArea.top - 50, // ABOVE the white canvas, not inside it
      
      background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, bgColor),
      text: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#ffffff]${text}[/color]`, {
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif'
      }),
      
      space: { left: 15, right: 15, top: 8, bottom: 8 }
    }).layout().setDepth(1500);
    
    // Auto-hide
    this.scene.time.delayedCall(duration, () => {
      if (this.scene.currentNotification) {
        this.scene.currentNotification.destroy();
        this.scene.currentNotification = null;
      }
    });
  }
  
  // Professional success notification using Rex UI Dialog with rich formatting
  showProfessionalSuccessNotification(roundName, levelNumberInRound, totalBonus, speedBonus) {
    try {
      console.log('🎉 showProfessionalSuccessNotification called:', { roundName, levelNumberInRound, totalBonus, speedBonus });
      
      // Calculate bonus details
      const clickPenalty = Math.max(0, ((this.scene.levelClicks || 0) - 1) * 5);
      const keystrokePenalty = Math.max(0, ((this.scene.levelKeystrokes || 0) - 5) * 2);
      const keyboardOnlyBonus = (this.scene.levelClicks === 0);
      const baseBonus = 100;
      
      // Clear any existing success dialog
      if (this.scene.successDialog) {
        this.scene.successDialog.destroy();
        this.scene.successDialog = null;
      }
      
      // Get slide area for proper positioning with fallback
      const slideArea = this.scene.slideArea || {
        x: this.scene.cameras.main.centerX || 400,
        top: 200,
        width: 720,
        height: 400
      };
      
      console.log('📍 Slide area info:', { 
        hasSlideArea: !!this.scene.slideArea, 
        slideArea, 
        cameraCenter: this.scene.cameras.main.centerX 
      });
      
      if (!this.scene.slideArea) {
        console.warn('⚠️ Slide area not available, using fallback positioning');
      }
    
    // Get top bar dimensions for sizing reference
    const topBarBottom = 110; // Top bar is at y=50 with height=60
    const availableHeight = slideArea.top - topBarBottom;
    
    // Use 60% of available height for the notification (reduced from 80%)
    const notificationHeight = availableHeight * 0.6;
    
    // Calculate vertical position to center in available space with equal gaps
    const verticalGap = (availableHeight - notificationHeight) / 2;
    // Position by center, ensuring proper spacing from white canvas
    const notificationY = topBarBottom + verticalGap + (notificationHeight / 2);
    
    // Calculate width as 80% of top bar width (reduced from 90%)
    const topBarWidth = this.topBar ? this.topBar.width : 720; // Default to 720 if no top bar
    const notificationWidth = topBarWidth * 0.8;
    
    // Create main container using Rex UI Label with proper sizer structure
    this.scene.successDialog = this.scene.rexUI.add.label({
      x: slideArea.x,
      y: notificationY,
      width: notificationWidth,
      height: notificationHeight,
      
      // Main background - refined dark green with subtle border
      background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 16, 0x1e7e34, 0x2ecc71, 3),
      
      // Use vertical sizer for proper layout control
      text: this.scene.rexUI.add.sizer({
        orientation: 'vertical',
        space: { item: 0 }
      }),
      
      space: { left: 0, right: 0, top: 0, bottom: 0 }
    });
    
    // Get the text container (which is our main sizer)
    const mainContainer = this.scene.successDialog.getElement('text');
    
    // HEADER BAR - Fixed at top with proper styling (reduced height)
    const headerBar = this.scene.rexUI.add.label({
      width: notificationWidth,
      height: 45, // Reduced from 60
      background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, { tl: 16, tr: 16, bl: 0, br: 0 }, 0x27ae60),
      text: this.scene.rexUI.add.BBCodeText(0, 0, '[b][color=#ffffff]✓ LEVEL COMPLETE[/color][/b]', {
        fontSize: '16px', // Reduced from 20px
        fontFamily: 'Arial, sans-serif',
        resolution: 4,
        halign: 'center'
      }),
      space: { left: 15, right: 15, top: 10, bottom: 10 }, // Reduced padding
      align: 'center'
    });
    
    // Add header bar to main container
    mainContainer.add(headerBar, { proportion: 0, align: 'center' });
    
    // Create content sizer for the two panels below header
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'horizontal',
      space: { item: 25 } // Reduced from 30
    });
    
    // LEFT PANEL (50% width) - centered within overall modal
    const leftPanelSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 12 } // Reduced from 16
    });
    
    // Add round name in refined green color (reduced font size)
    const roundNameText = this.scene.rexUI.add.BBCodeText(0, 0, `[b][size=18][color=#90EE90]${roundName}[/color][/size][/b]`, {
      fontSize: '18px', // Reduced from 22px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5, // Adjusted width calculation
      halign: 'center'
    });
    leftPanelSizer.add(roundNameText, { proportion: 0, align: 'center' });
    
    // Add "Level X Complete" text - refined styling (reduced font size)
    const levelCompleteText = this.scene.rexUI.add.BBCodeText(0, 0, `[color=#ffffff]Level ${levelNumberInRound} Complete[/color]`, {
      fontSize: '15px', // Reduced from 18px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'center'
    });
    leftPanelSizer.add(levelCompleteText, { proportion: 0, align: 'center' });
    
    // Add single, clean checkmark with "COMPLETE" - FIXED alignment (reduced font size)
    const checkmarkText = this.scene.rexUI.add.BBCodeText(0, 0, '[size=22][color=#00FF00]✓ COMPLETE[/color][/size]', {
      fontSize: '22px', // Reduced from 28px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'center'
    });
    leftPanelSizer.add(checkmarkText, { proportion: 0, align: 'center' });
    
    // RIGHT PANEL (50% width) - refined layout
    const rightPanelSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 8 } // Reduced from 12
    });
    
    // Score breakdown title in refined gold color (reduced font size)
    const scoreTitleText = this.scene.rexUI.add.BBCodeText(0, 0, '[b][size=15][color=#FFD700]Score Breakdown[/color][/size][/b]', {
      fontSize: '15px', // Reduced from 18px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'left'
    });
    rightPanelSizer.add(scoreTitleText, { proportion: 0, align: 'left' });
    
    // Base score in refined white (reduced font size)
    const baseScoreText = this.scene.rexUI.add.BBCodeText(0, 0, `[color=#ffffff]Base Score: ${baseBonus} pts[/color]`, {
      fontSize: '14px', // Reduced from 16px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'left'
    });
    rightPanelSizer.add(baseScoreText, { proportion: 0, align: 'left' });
    
    // Add bonuses/penalties with refined spacing (reduced font sizes)
    if (speedBonus > 0) {
      const speedBonusText = this.scene.rexUI.add.BBCodeText(0, 0, `[color=#00FF00]Speed Bonus: +${speedBonus} pts[/color]`, {
        fontSize: '14px', // Reduced from 16px
        fontFamily: 'Arial, sans-serif',
        resolution: 4,
        fixedWidth: (notificationWidth - 100) * 0.5,
        halign: 'left'
      });
      rightPanelSizer.add(speedBonusText, { proportion: 0, align: 'left' });
    }
    
    if (keyboardOnlyBonus) {
      const keyboardBonusText = this.scene.rexUI.add.BBCodeText(0, 0, '[color=#00FF00]⌨ Keyboard Only (1.5x)[/color]', {
        fontSize: '14px', // Reduced from 16px
        fontFamily: 'Arial, sans-serif',
        resolution: 4,
        fixedWidth: (notificationWidth - 100) * 0.5,
        halign: 'left'
      });
      rightPanelSizer.add(keyboardBonusText, { proportion: 0, align: 'left' });
    }
    
    if (clickPenalty > 0 || keystrokePenalty > 0) {
      // Add penalties header with refined spacing (reduced font size)
      const penaltiesText = this.scene.rexUI.add.BBCodeText(0, 0, '[color=#FF6B6B]Penalties:[/color]', {
        fontSize: '14px', // Reduced from 16px
        fontFamily: 'Arial, sans-serif',
        resolution: 4,
        fixedWidth: (notificationWidth - 100) * 0.5,
        halign: 'left'
      });
      rightPanelSizer.add(penaltiesText, { proportion: 0, align: 'left', padding: { top: 6 } }); // Reduced padding
      
      if (clickPenalty > 0) {
        const clickPenaltyText = this.scene.rexUI.add.BBCodeText(0, 0, `[color=#FF6B6B]  Mouse clicks: -${clickPenalty}[/color]`, {
          fontSize: '14px', // Reduced from 16px
          fontFamily: 'Arial, sans-serif',
          resolution: 4,
          fixedWidth: (notificationWidth - 100) * 0.5,
          halign: 'left'
        });
        rightPanelSizer.add(clickPenaltyText, { proportion: 0, align: 'left' });
      }
      
      if (keystrokePenalty > 0) {
        const keystrokePenaltyText = this.scene.rexUI.add.BBCodeText(0, 0, `[color=#FF6B6B]  Extra keys: -${keystrokePenalty}[/color]`, {
          fontSize: '14px', // Reduced from 16px
          fontFamily: 'Arial, sans-serif',
          resolution: 4,
          fixedWidth: (notificationWidth - 100) * 0.5,
          halign: 'left'
        });
        rightPanelSizer.add(keystrokePenaltyText, { proportion: 0, align: 'left' });
      }
    }
    
    // Add refined separator line with REDUCED spacing (reduced font size)
    const separatorText = this.scene.rexUI.add.BBCodeText(0, 0, '[color=#888888]─────────────────[/color]', {
      fontSize: '14px', // Reduced from 16px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'left'
    });
    rightPanelSizer.add(separatorText, { proportion: 0, align: 'left', padding: { top: 3, bottom: 3 } }); // Reduced padding
    
    // Add total score with refined styling (reduced font size)
    const totalScoreText = this.scene.rexUI.add.BBCodeText(0, 0, `[b][size=18][color=#FFD700]Total: ${totalBonus} pts[/color][/size][/b]`, {
      fontSize: '18px', // Reduced from 22px
      fontFamily: 'Arial, sans-serif',
      resolution: 4,
      fixedWidth: (notificationWidth - 100) * 0.5,
      halign: 'left'
    });
    rightPanelSizer.add(totalScoreText, { proportion: 0, align: 'left' });
    
    // Add both panels to content sizer with refined alignment
    contentSizer.add(leftPanelSizer, { proportion: 1, align: 'center', expand: true });
    contentSizer.add(rightPanelSizer, { proportion: 1, align: 'top', expand: true });
    
    // Add content sizer below header using main sizer
    mainContainer.add(contentSizer, { proportion: 1, align: 'center', expand: true });
    
    // Layout the main container properly
    this.scene.successDialog.layout();
    
    // Set depth to ensure visibility
    this.scene.successDialog.setDepth(2000);
    
    // Refined fade in animation
    this.scene.successDialog.setAlpha(0);
    this.scene.successDialog.setScale(0.98);
    this.scene.tweens.add({
      targets: this.scene.successDialog,
      alpha: 1,
      scaleX: 1.0,
      scaleY: 1.0,
      duration: 350,
      ease: 'Power2.Out'
    });
    
    // Auto-hide after 4 seconds
    this.scene.time.delayedCall(4000, () => {
      if (this.scene.successDialog) {
        this.scene.tweens.add({
          targets: this.scene.successDialog,
          alpha: 0,
          scaleX: 0.98,
          scaleY: 0.98,
          duration: 250,
          ease: 'Power2.In',
          onComplete: () => {
            if (this.scene.successDialog) {
              this.scene.successDialog.destroy();
              this.scene.successDialog = null;
            }
          }
        });
      }
    });
    
    console.log('✅ Professional notification created successfully');
    
    } catch (error) {
      console.error('❌ Error creating professional notification:', error);
      console.log('🔄 Falling back to simple notification');
      
      // Fallback to simple notification if professional one fails
      this.showNotification(`✓ ${roundName} Level ${levelNumberInRound} Complete! +${totalBonus} pts`, 'success', 4000);
    }
  }
}